import { MathsKitElement, MathsKitState, MathsKitType } from './types';
import { composeTransforms, createScaleMatrix, createTranslationMatrix, decomposeMatrix, Matrix2D, Point2d } from './utils';
import { MathsKitManager } from './manager';
import { LineSegmentLike } from './utils/LineSegment';
import { CircleSegmentLike } from './utils/CircleSegment';

export interface MathsKitOptions {
    id: string;
    type: MathsKitType;
    state: MathsKitState;
}

export type MarkLineType = 'x' | 'y' | 'circle';
export type MarkDirection = 'left' | 'right' | 'top' | 'bottom' | 'topLeftToBottomRight' | 'topRightToBottomLeft';

export type MarkLine = {
  axis: MarkLineType;
  direction: MarkDirection;
  line: LineSegmentLike;
}

export type MarkCircle = {
  axis: MarkLineType;
  circle: CircleSegmentLike;
}

export type MarkLineLike = MarkLine | MarkCircle;

export abstract class MathsKit {
  readonly id: string;
  private _state: MathsKitState;
  readonly element!: MathsKitElement;
  readonly manager: MathsKitManager;
  protected _isActive: boolean = true;
  constructor(options: MathsKitOptions, manager: MathsKitManager) {
    this.id = options.id;
    this._state = options.state;
    this.manager = manager;
    this.element = this.createElement() as MathsKitElement;
    this.bindElement(this.element, options.type);
    // 初始化主题变量
    this.setThemeVariables(this.manager.theme);
  }

  init(){
    this.render();
  }

  bindElement(element: MathsKitElement, type: MathsKitType) {
    element.classList.add('maths-kit', `maths-kit-${type}`, `maths-kit-${this.manager.theme}`);
    element.setAttribute('data-type', 'Drag');
    element.setAttribute('data-id', this.id);
    this.manager.container.appendChild(element);
  }

  getStretchDirection(): 'x' | 'y' {
    return 'x';
  }

  setActive(isActive: boolean) {
    this._isActive = isActive;
    if (isActive) {
      this.element.style.pointerEvents = 'auto';
    } else {
      this.element.style.pointerEvents = 'none';
    }
  }

  setTheme(theme: 'light' | 'dark') {
    const currentTheme = this.element.classList.contains('maths-kit-light') ? 'light' :  this.element.classList.contains('maths-kit-dark') ? 'dark' : null;
    if (currentTheme !== theme) {
      if (currentTheme) {
        this.element.classList.remove(`maths-kit-${currentTheme}`);
      }
      this.element.classList.add(`maths-kit-${theme}`);
      
      // 设置主题相关的CSS变量
      this.setThemeVariables(theme);
    }
  }

  private setThemeVariables(theme: 'light' | 'dark') {
    const variables = theme === 'light' ? this.getLightThemeVariables() : this.getDarkThemeVariables();
    
    Object.entries(variables).forEach(([key, value]) => {
      this.element.style.setProperty(key, value);
    });
  }

  private getLightThemeVariables() {
    return {
      '--maths-kit-ruler-bg': 'rgba(255, 255, 255, 0.8)',
      '--maths-kit-ruler-border': 'rgba(233, 233, 233, 0.8)',
      '--maths-kit-ruler-mark-bg': 'rgba(233, 233, 233, 0.8)',
      '--maths-kit-ruler-major-mark': '#000',
      '--maths-kit-ruler-medium-mark': '#666',
      '--maths-kit-ruler-minor-mark': '#999',
      '--maths-kit-ruler-text': '#000',
      '--maths-kit-ruler-unit-text': '#666',
      '--maths-kit-math-kit-btn-bg': '#fff',
      '--maths-kit-math-kit-btn-hover-bg': '#f0f4ff',
      '--maths-kit-math-kit-angle-btn-bg': '#00A870',
      '--maths-kit-math-kit-btn-border': '#eee',
      '--maths-kit-math-kit-btn-text': '#000',
      '--maths-kit-math-kit-btn-shadow': 'rgba(0, 0, 0, 0.2)',
    };
  }

  private getDarkThemeVariables() {
    return {
      '--maths-kit-ruler-bg': 'rgba(40, 40, 40, 0.9)',
      '--maths-kit-ruler-border': 'rgba(80, 80, 80, 0.8)',
      '--maths-kit-ruler-mark-bg': 'rgba(60, 60, 60, 0.8)',
      '--maths-kit-ruler-major-mark': '#fff',
      '--maths-kit-ruler-medium-mark': '#ccc',
      '--maths-kit-ruler-minor-mark': '#999',
      '--maths-kit-ruler-text': '#fff',
      '--maths-kit-ruler-unit-text': '#ccc',
      '--maths-kit-math-kit-btn-bg': '#fff',
      '--maths-kit-math-kit-btn-hover-bg': 'rgba(35, 108, 250, 0.08)',
      '--maths-kit-math-kit-angle-btn-bg': '#00A870',
      '--maths-kit-math-kit-btn-border': '#eee',
      '--maths-kit-math-kit-btn-text': '#000',
      '--maths-kit-math-kit-btn-shadow': 'rgba(0, 0, 0, 0.8)',
    };
  }

  get state(): MathsKitState {
    return this._state;
  }

  getDomMatrix = (): Matrix2D => {
    const originMatrix = new Matrix2D(...this.state.matrix);
    const transform = decomposeMatrix(originMatrix);
    const scaleMatrix = createScaleMatrix(this.manager.globalScale, this.manager.globalScale);
    const scaleTranslateMatrix = createTranslationMatrix(transform.translateX * this.manager.globalScale, transform.translateY * this.manager.globalScale);
    const matirx = composeTransforms(originMatrix, scaleMatrix, scaleTranslateMatrix);
    return matirx;
  };

  update(state: MathsKitState) {
    this._state = state;
    this.render();
  }

  destroy() {
    if (this.element) {
      this.element.remove();
    }
  }

  toTop(){
    if (this.manager.isTopMathsKit(this.id)) {
      return;
    }
    this.manager.update(this.id, { zIndex: this.manager.topZIndex + 1 });
  }

  updateStateMatrix(currentMatrix: Matrix2D) {
    const { translateX, translateY, scaleX, scaleY, rotation } = decomposeMatrix(currentMatrix);
    const { x,y } = this.state;
    const container = this.manager.container;
    const containerWidth = container.clientWidth;
    const containerHeight = container.clientHeight;
    const newX = translateX / containerWidth + x;
    const newY = translateY / containerHeight + y;
    const scaleMatrix = createScaleMatrix(scaleX, scaleY);
    const cos = Math.cos(rotation); // 使用逆时针向旋转
    const sin = Math.sin(rotation);
    const transformedDragX = translateX * cos - translateY * sin;
    const transformedDragY = translateX * sin + translateY * cos;
    const translateMatrix = createTranslationMatrix(transformedDragX, transformedDragY);
    const matirx = composeTransforms(currentMatrix, scaleMatrix.inverse(), translateMatrix.inverse());
    this.manager.update(this.id, { x: newX, y: newY, matrix: matirx.toArray() });
  }
  abstract getMarkLines(): MarkLineLike[];
  abstract createElement(): MathsKitElement;
  abstract render(): void;
  abstract showLineLength(number: number): void;
  abstract hideLineLength(): void;

  abstract onDragStart(): void;
  abstract onDragDoing(): void;
  abstract onDragEnd(matrix: Matrix2D): void;
  abstract onRotateStart(originAngle: number): void;
  abstract onRotateDoing(angle: number): void;
  abstract onRotateEnd(matrix: Matrix2D): void;
  abstract onStretchStart(): void;
  abstract onStretchDoing(width: number): void;
  abstract onStretchEnd(width: number): void;
  abstract getRotateCenterPoint(): Point2d;
}
